import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api/client';
import { paymentKeys } from '@/lib/queryKeys';

/**
 * What the customer is charged ON TOP of the amount, for the exact instrument
 * an operator is about to charge.
 *
 * Server-computed on purpose. The fee is not `amount x percent`: it is prorated
 * to the invoice's pre-tax subtotal, clamped to the 3% card-network ceiling,
 * and clamped again to the tenant's actual cost of accepting the charge — and
 * only the server holds the processor fee schedule that last clamp needs. A
 * browser-side multiplication overstates the fee whenever the cost cap binds,
 * which is exactly the "disclosed fee did not match the charged fee" class of
 * bug the public payment surfaces already stamped out.
 */
export interface CardServiceFeeQuote {
  achCheckDiscountEnabled?: boolean;
  achCheckDiscountPercent?: number;
  discountCents?: number;
  chargeCents?: number;
  /** The tenant charges a card service fee at all. */
  enabled: boolean;
  /** THIS instrument can carry one — credit cards only, never debit or bank. */
  surchargeable: boolean;
  /** Network-capped rate, for rate-only copy. */
  surchargePercent: number;
  /**
   * The exact fee in cents. `0` when no fee applies; `null` when a fee applies
   * but the server could not pin the amount — render the rate, never a guess.
   */
  feeCents: number | null;
}

export interface CardServiceFeeQuoteParams {
  amount: number;
  /**
   * The saved method's stored `type` (CREDIT_CARD / DEBIT_CARD / ACH /
   * BANK_ACCOUNT). Preferred over `paymentMethod`: both processors record it
   * funding-aware, so it is what decides credit-card-only.
   */
  paymentMethodType?: string | null;
  /** Coarse rail, when the charge has no saved method behind it. */
  paymentMethod?: string | null;
  /** Supplies the pre-tax split the fee is prorated against. */
  invoiceId?: string | null;
  provider?: string | null;
  enabled?: boolean;
}

export function useCardServiceFeeQuote({
  amount,
  paymentMethodType,
  paymentMethod,
  invoiceId,
  provider,
  enabled = true,
}: CardServiceFeeQuoteParams) {
  const params = {
    amount,
    ...(paymentMethodType ? { paymentMethodType } : {}),
    ...(paymentMethod ? { paymentMethod } : {}),
    ...(invoiceId ? { invoiceId } : {}),
    ...(provider ? { provider } : {}),
  };

  return useApiQuery<CardServiceFeeQuote>({
    queryKey: paymentKeys.serviceFeeQuote(params),
    queryFn: () =>
      apiGet<CardServiceFeeQuote>(operationsClient, '/payments/gateway/service-fee/quote', {
        params,
      }),
    enabled: enabled && amount > 0 && Boolean(paymentMethodType || paymentMethod),
    showErrorToast: false,
  });
}
